<template>
  <!-- Ejemplo editable de la hoja del campo: encuestas inventadas por orden de llegada (valor y formulario
       se pueden cambiar), cómo queda el campo tras cada una y la matriz «¿Y con otra regla?», que aplica
       la regla al pulsar una casilla. Todo se calcula con la copia del motor de recipe.js. -->
  <section class="rx">
    <div class="rx-head">
      <h4 class="fs-glabel">{{ t('ex.title') }}</h4>
      <label class="rx-start">{{ t('ex.start') }}
        <select v-model="startMode" class="in rx-sel">
          <option value="new">{{ t('ex.startNew') }}</option>
          <option value="had">{{ t('ex.startHad', { v: seed.startValue }) }}</option>
        </select>
      </label>
    </div>
    <p class="rx-hint">{{ t('ex.hint') }}</p>

    <ol class="rx-rows">
      <li v-for="(r, i) in result.rows" :key="i" class="rx-row" :class="rowClass(r)">
        <span class="rx-day mono">{{ r.dayLabel }}</span>
        <select class="rx-form" :value="events[i].questId" :disabled="formChoices.length < 2"
                :aria-label="t('ex.formOf', { day: r.dayLabel })" @change="setForm(i, $event.target.value)">
          <option v-for="f in formChoices" :key="f.questId" :value="f.questId">P{{ f.priority }} · {{ f.name }}</option>
        </select>
        <input class="in rx-in" :value="seed.sysFirst ? r.raw : events[i].raw" :readonly="!!seed.sysFirst" spellcheck="false"
               :aria-label="t('ex.valueOf', { day: r.dayLabel })" @input="events[i].raw = $event.target.value" />
        <span class="rx-flow" :title="dash(r.beforeText) + ' → ' + dash(r.afterText)">
          <span>{{ dash(r.beforeText) }}</span><i aria-hidden="true">{{ r.beforeText === r.afterText ? '=' : '→' }}</i><b>{{ dash(r.afterText) }}</b>
        </span>
        <span class="rx-why">
          {{ r.silentText ? r.silentText + ' · ' + r.text : r.text }}<abbr v-if="r.thousands" class="rx-bang" :title="t('conv.thousands')">!</abbr>
        </span>
      </li>
    </ol>
    <div class="rx-result" aria-live="polite">{{ t('ex.result', { v: dash(result.finalText) }) }}</div>

    <div class="rx-mx" role="group" :aria-labelledby="uid + '-mx'">
      <h4 :id="uid + '-mx'" class="fs-glabel">{{ t('ex.matrix') }}</h4>
      <p class="rx-hint">{{ t('ex.matrixHint') }}</p>
      <table class="rx-mt">
        <thead>
          <tr><td /><th v-for="o in MISSING" :key="o" scope="col">{{ t('seg.' + o) }}</th></tr>
        </thead>
        <tbody>
          <tr v-for="m in MODES" :key="m">
            <th scope="row">{{ t('merge.' + m) }}</th>
            <td v-for="o in MISSING" :key="o">
              <button type="button" class="rx-cell" :class="{ on: isCurrent(m, o), flag: flags[m] && flags[m][o] }"
                      :aria-pressed="isCurrent(m, o)" :title="cellTitle(m, o)"
                      @click="emit('rule', { mergeMode: m, onMissing: o })">
                <span class="rx-cv">{{ dash(showValue(family, matrix[m][o])) }}</span>
                <span v-if="flags[m] && flags[m][o]" class="rx-bang" aria-hidden="true">!</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { t } from '@vem/i18n.js';
import { ruleFlags, ruleMatrix, runExample, showValue } from '@vem/recipe.js';

let seq = 0;

const props = defineProps({
  /** recipe.example: {family, isText, sys, sysFirst, typeName, startValue, events: [{day, dayLabel, questId, prio, raw}]}. */
  seed: { type: Object, required: true },
  /** Regla actual del campo: {mergeMode, onMissing}; null = last/keep. */
  rule: { type: Object, default: null },
  /** recipe.perForm: [{questId, name, priority, state}]. */
  forms: { type: Array, default: () => [] },
});
const emit = defineEmits(['rule']);

const MODES = ['last', 'first', 'priority', 'concat'];
const MISSING = ['keep', 'clear'];
const uid = `rx-${++seq}`;

const events = ref([]);
const startMode = ref('new');

// Se reinicia solo si cambia la semilla (otras preguntas o formularios), no al cambiar la regla.
watch(() => JSON.stringify(props.seed), () => {
  events.value = props.seed.events.map((e) => ({ ...e }));
  startMode.value = 'new';
}, { immediate: true });

const family = computed(() => props.seed.family);
const sys = computed(() => props.seed.sys || null);
const mode = computed(() => props.rule?.mergeMode || 'last');
const clear = computed(() => props.rule?.onMissing === 'clear');
const formChoices = computed(() => props.forms.filter((f) => f.state === 'feeds' || f.state === 'silent'));
const stateOf = (questId) => props.forms.find((f) => f.questId === questId)?.state || null;
const nameOf = (questId) => props.forms.find((f) => f.questId === questId)?.name || questId;
const start = computed(() => (startMode.value === 'had' ? props.seed.startValue : null));

const base = computed(() => ({
  events: events.value, start: start.value, family: family.value, sys: sys.value, stateOf, typeName: props.seed.typeName, formName: nameOf,
}));
const result = computed(() => runExample({ ...base.value, mode: mode.value, clear: clear.value }));
const matrix = computed(() => ruleMatrix(base.value));
const flags = computed(() => {
  const feeds = props.forms.filter((f) => f.state === 'feeds');
  return ruleFlags({ family: family.value, N: formChoices.value.length, ladderLen: new Set(feeds.map((f) => f.priority || 1)).size });
});

function setForm(i, questId) {
  const f = props.forms.find((x) => x.questId === questId);
  events.value[i] = { ...events.value[i], questId, prio: f ? f.priority || 1 : events.value[i].prio };
}

const isCurrent = (m, o) => mode.value === m && (clear.value ? 'clear' : 'keep') === o;

function cellTitle(m, o) {
  const name = `${t('merge.' + m)} · ${t('seg.' + o)}`;
  return flags.value[m] && flags.value[m][o] ? `${name} · ${t('ex.flag')}` : name;
}

const dash = (v) => (v == null || v === '' ? '—' : v);

function rowClass(r) {
  return [r.code, { silent: r.silent, changed: r.beforeText !== r.afterText }];
}
</script>

<style>
.vem-root .rx { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--line2); }
.vem-root .rx-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.vem-root .rx-head .fs-glabel { margin: 0; }
.vem-root .rx-start { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: var(--tx2); }
.vem-root .rx-sel { min-height: 28px; padding: 3px 6px; font-size: 12px; max-width: 260px; }
.vem-root .rx-hint { margin: 4px 0 10px; font-size: 11.5px; color: var(--tx3); line-height: 1.4; }
.vem-root .rx-rows { list-style: none; margin: 0; padding: 0; border: 1px solid var(--line2); border-radius: 11px; overflow: hidden; background: var(--panel); }
.vem-root .rx-row {
  display: grid; grid-template-columns: 50px minmax(0, 1fr) 96px minmax(84px, auto);
  grid-template-areas: 'day form val flow' '. why why why'; column-gap: 8px; row-gap: 1px; align-items: center;
  padding: 6px 10px 7px; border-top: 1px solid var(--line); transition: background .15s ease;
}
.vem-root .rx-row:first-child { border-top: 0; }
.vem-root .rx-day { grid-area: day; font-size: 11px; color: var(--tx3); white-space: nowrap; }
.vem-root .rx-form {
  grid-area: form; min-width: 0; max-width: 100%; min-height: 26px; padding: 2px 4px; font: inherit; font-size: 12px; color: var(--tx);
  background: none; border: 1px solid transparent; border-radius: 7px; cursor: pointer; text-overflow: ellipsis;
}
.vem-root .rx-form:hover:not(:disabled) { border-color: var(--line2); background: var(--ink); }
.vem-root .rx-form:disabled { cursor: default; opacity: 1; color: var(--tx); appearance: none; -webkit-appearance: none; }
.vem-root .rx-in { grid-area: val; width: 100%; min-height: 27px; padding: 2px 7px; font-family: var(--mono); font-size: 12px; }
.vem-root .rx-in[readonly] { background: var(--panel2); color: var(--tx2); }
.vem-root .rx-flow {
  grid-area: flow; display: inline-flex; justify-content: flex-end; align-items: baseline; gap: 5px; min-width: 0;
  font-family: var(--mono); font-size: 12px; color: var(--tx3); white-space: nowrap; overflow: hidden;
}
.vem-root .rx-flow span, .vem-root .rx-flow b { overflow: hidden; text-overflow: ellipsis; max-width: 160px; }
.vem-root .rx-flow i { font-style: normal; color: var(--tx3); }
.vem-root .rx-flow b { color: var(--tx); font-weight: 600; }
.vem-root .rx-row.changed .rx-flow b { color: var(--mint); }
.vem-root .rx-why { grid-area: why; font-size: 11.5px; color: var(--tx2); line-height: 1.35; }
.vem-root .rx-row.silent .rx-why, .vem-root .rx-row.keep .rx-why { color: var(--tx3); }
.vem-root .rx-row.clear .rx-why { color: var(--amber); }
.vem-root .rx-row.error { background: var(--danger-bg, rgba(192, 57, 43, .07)); }
.vem-root .rx-row.error .rx-why { color: var(--danger); }
.vem-root .rx-row.error .rx-in { border-color: var(--danger-bd); }
.vem-root .rx-bang { margin-left: 4px; color: var(--amber); font-weight: 700; text-decoration: none; cursor: help; }
.vem-root .rx-result {
  display: flex; align-items: baseline; gap: 6px; margin-top: 8px; padding: 8px 12px; border-radius: 9px;
  background: var(--mint-bg); color: var(--tx); font-weight: 600; font-size: 13px; overflow-wrap: anywhere;
}
.vem-root .rx-mx { margin-top: 18px; }
.vem-root .rx-mx .fs-glabel { margin: 0; }
.vem-root .rx-mt { width: calc(100% + 8px); margin: -4px; border-collapse: separate; border-spacing: 4px; table-layout: fixed; font-size: 12.5px; }
.vem-root .rx-mt td:first-child, .vem-root .rx-mt th[scope='row'] { width: 40%; }
.vem-root .rx-mt th { font-weight: 500; color: var(--tx2); text-align: left; padding: 0 4px; overflow-wrap: anywhere; }
.vem-root .rx-mt thead th { font-family: var(--mono); font-size: 9.5px; letter-spacing: .12em; text-transform: uppercase; color: var(--tx3); text-align: center; }
.vem-root .rx-cell {
  display: flex; align-items: center; justify-content: center; gap: 2px; width: 100%; min-height: 32px; padding: 4px 8px;
  font-family: var(--mono); font-size: 12px; color: var(--tx); background: var(--panel); border: 1px solid var(--line2);
  border-radius: 8px; cursor: pointer; transition: border-color .15s ease, background .15s ease, color .15s ease;
}
.vem-root .rx-cv { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vem-root .rx-cell:hover { border-color: var(--mint); background: var(--mint-bg); }
.vem-root .rx-cell.flag { border-color: var(--amber-bd); }
.vem-root .rx-cell.on { background: var(--teal); border-color: var(--teal); color: #fff; font-weight: 600; box-shadow: 0 6px 14px -8px var(--teal); }
.vem-root .rx-cell.on .rx-bang { color: #ffe1a8; }
</style>
